<script lang="ts">
  import { fly } from "svelte/transition";

  import Info from "../Reusables/Info.svelte";
  import Grid from "../Reusables/Grid.svelte";
  import GridCell from "../Reusables/GridCell.svelte";
  import CleanLayout from "../Reusables/CleanLayout.svelte";
  import CleanTopper from "../Reusables/CleanTopper.svelte";
  import CleanFooter from "../Reusables/CleanFooter.svelte";
  import CleanOverlay from "../Reusables/CleanOverlay.svelte";
  import CleanChat from "../Reusables/CleanChat.svelte";
  import type {
    FillerPlayer,
    FillerState,
    User,
  } from "../../../server/interfaces";
  import { WebSocket, in_game, show_chat, Utils } from "../stores";
  import { fix_state } from "../helpers";

  let game: FillerState;
  let self: FillerPlayer;
  let self_profile: User;
  let opponent: FillerPlayer;
  let opponent_profile: User;

  let messages: Array<{
    user: User;
    content: string;
    time: number;
    spectator: boolean;
  }> = [];

  let new_message = false;
  $WebSocket.on("OnMessage", (msg) => {
    if (
      messages.length == 0 ||
      messages[messages.length - 1].time != msg.time
    ) {
      messages = messages.concat([msg]);
      new_message = !$show_chat;
    }
  });

  let player_count = 0;
  Utils.GameStats.subscribe((e) => {
    if (e) {
      player_count = e.total_players;
    }
  });

  $WebSocket.on("OnGameUpdate", (game_state) => {
    let gs = <FillerState>game_state;
    game = gs;
    self = gs.state === "Spectator" ? gs.player1 : <FillerPlayer>gs.self;
    if (gs.player2)
      opponent = gs.player2.id === self.id ? gs.player1 : gs.player2;
    if (self) self_profile = self.user;
    if (opponent) opponent_profile = opponent.user;
    in_game.set(true);
  });

  function leave_game() {
    $WebSocket.emit("LeaveRoom");
    window.history.replaceState({}, document.title, window.location.pathname);
    in_game.set(false);

    game = null;
    self = null;
    self_profile = null;
    opponent = null;
    opponent_profile = null;
  }

  function on_create_room(name: string): Promise<string> {
    return new Promise((resolve, reject) => {
      $WebSocket.emit("CreateRoom", name, "Filler", (room_id) => {
        resolve(room_id);
      });
    });
  }

  function on_join_room(room: string, name: string): Promise<string> {
    return new Promise((resolve, reject) => {
      $WebSocket.emit("JoinRoom", room, name, (room_id) => {
        resolve(room_id);
      });
    });
  }
</script>

<div class="game">
  {#if !$in_game}
    <Info GameName="Filler" {player_count} {on_create_room} {on_join_room} />
  {:else}
    <CleanLayout GameName="Filler" show_chat={$show_chat}>
      <CleanTopper
        slot="topper"
        {leave_game}
        room_id={game.room_id}
        spectating={game.state === "Spectator"}
        spectators={game.spectator_count}
      >
        <i
          class={`fas fa-comment${new_message ? "-dots" : ""} ctog`}
          on:click={() => {
            show_chat.set(!$show_chat);
            new_message = false;
          }}
        />
      </CleanTopper>

      <CleanChat
        slot="chat"
        w={340}
        h={380}
        m={10}
        {messages}
        send_func={(content) => {
          $WebSocket.emit("SendMessage", content);
        }}
      />

      <CleanOverlay
        slot="overlay"
        title={fix_state(self.state)}
        wins_2={opponent ? opponent.wins : 0}
        wins_1={self ? self.wins : 0}
        {leave_game}
        rematch={() => $WebSocket.emit("Rematch")}
        show={self.state !== "ongoing"}
        show_history={self.state !== "waiting"}
        show_actions={!(game.state === "Spectator")}
        show_rematch={self.state !== "forfeit"}
      />

      <Grid slot="game" x={8} y={7}>
        {#each game.board as r, y}
          {#each r as cell, x}
            <div class={`filler filler-${cell} cell`}>
              {self && self.owned_cells.some((e) => e.x == x && e.y == y)
                ? "😄"
                : ""}
              {opponent &&
              opponent.owned_cells.some((e) => e.x == x && e.y == y)
                ? "😡"
                : ""}
            </div>
          {/each}
        {/each}
      </Grid>

      <CleanFooter
        slot="footer"
        player_2={!opponent
          ? "invite a friend"
          : `${opponent ? opponent.owned_cells.length : 0}   ${
              opponent_profile.name
            }`}
        player_1={`${self_profile.name}   ${
          self ? self.owned_cells.length : 0
        }`}
        indicate_2={opponent && opponent.turn && self.state === "ongoing"
          ? "Opponent's Turn"
          : opponent && opponent.rematch
          ? "Wants to rematch"
          : ""}
        indicate_1={self.turn && self.state === "ongoing"
          ? `${game.state === "Spectator" ? "Opponents" : "Your"} Turn`
          : self.rematch
          ? "Wants to rematch"
          : ""}
      >
        <div
          slot="icon_1"
          class={`filler-small filler-${
            self && self.owned_cells.length != 0
              ? game.board[self.owned_cells[0].y][self.owned_cells[0].x]
              : game.board[0][7]
          }`}
          style="width: 25px; height: 25px"
        />
        <div
          slot="icon_2"
          class={`filler-small filler-${
            opponent && opponent.owned_cells.length != 0
              ? game.board[opponent.owned_cells[0].y][opponent.owned_cells[0].x]
              : game.board[0][7]
          }`}
          style="width: 25px; height: 25px"
        />
      </CleanFooter>
      <div slot="bottom-bar">
        {#each Array(6) as _, i}
          <button
            class={`special-btn filler-btn filler-${i + 1}`}
            on:click={() => $WebSocket.emit("Turn", i + 1)}
            disabled={game.state === "Spectator" ||
              !self.turn ||
              i + 1 == self.piece ||
              (opponent && i + 1 == opponent.piece)}
          />
        {/each}
      </div>
    </CleanLayout>
  {/if}
</div>

<style>
  .game {
    overflow: hidden;
  }

  .fa-comment-dots {
    animation: float 800ms ease-in-out;
  }

  .filler-btn {
    width: 2.5rem;
    height: 2.5rem;
    margin: 5px;
  }

  .filler {
    border-radius: 2px;
    margin: 2px 2px;
    transform: translateY(-4px);
    /* box-shadow: 0px 4px 0px; */
  }

  .cell {
    color: black !important;
    opacity: 1;
    font-weight: normal;
    padding-top: 7px;
    /* opacity: 0.5; */
    /* animation: fading 2s ease-in-out infinite; */
  }

  .filler-small {
    border-radius: 2px;
  }

  .filler-5 {
    color: #2e88bd;
    background-color: #41b9ff;
  }
  .filler-6 {
    color: #4d1a6e;
    background-color: #832ebc;
  }
  .filler-2 {
    color: #b16701;
    background-color: #ff9500;
  }
  .filler-1 {
    color: #9e213a;
    background-color: #f33057;
  }
  .filler-4 {
    color: #7aac1e;
    background-color: #b6ff2f;
  }
  .filler-3 {
    color: #ac970e;
    background-color: #fedf16;
  }
</style>
